import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { adminApi, ApiError } from '@/lib/api'
import { clearAdminToken, getAdminToken, setAdminToken } from '@/lib/db'

interface AdminSessionValue {
  unlocked: boolean
  /** False when no passcode has been set on this deployment yet. */
  configured: boolean
  checking: boolean
  unlock: (passcode: string) => Promise<void>
  lock: () => Promise<void>
}

const AdminSessionContext = createContext<AdminSessionValue | null>(null)

/**
 * Admin lock state.
 *
 * The previous app had `const ADMIN_PASSWORD = "admin"` compared in the
 * browser — anyone could read it out of the JavaScript and unlock. This keeps
 * the same lock-icon interaction but the passcode is only ever checked by the
 * Worker, and what the client holds afterwards is a session token that the
 * server can expire or revoke.
 */
export function AdminSessionProvider({ children }: { children: ReactNode }) {
  const [unlocked, setUnlocked] = useState(false)
  const [configured, setConfigured] = useState(true)
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    let cancelled = false

    void (async () => {
      try {
        const [status, token] = await Promise.all([adminApi.status(), getAdminToken()])
        if (cancelled) return
        setConfigured(status.configured)
        // Holding a token is not proof of a live session — it may have expired
        // server-side. The first admin call will 401 and clear it if so.
        setUnlocked(token !== null)
      } catch {
        // Offline, or the endpoint is unreachable. Assume configured so the UI
        // offers the passcode prompt rather than an alarming setup message.
        if (!cancelled) setConfigured(true)
      } finally {
        if (!cancelled) setChecking(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  const unlock = useCallback(async (passcode: string) => {
    const result = await adminApi.login(passcode)
    await setAdminToken(result.token)
    setUnlocked(true)
  }, [])

  const lock = useCallback(async () => {
    try {
      // Best effort: delete the session server-side too, so a copied token from
      // this device stops working rather than lingering until it expires.
      await adminApi.logout()
    } catch (err) {
      if (!(err instanceof ApiError)) throw err
    } finally {
      await clearAdminToken()
      setUnlocked(false)
    }
  }, [])

  const value = useMemo<AdminSessionValue>(
    () => ({ unlocked, configured, checking, unlock, lock }),
    [unlocked, configured, checking, unlock, lock],
  )

  return <AdminSessionContext.Provider value={value}>{children}</AdminSessionContext.Provider>
}

export function useAdminSession(): AdminSessionValue {
  const ctx = useContext(AdminSessionContext)
  if (ctx === null) throw new Error('useAdminSession must be used inside <AdminSessionProvider>')
  return ctx
}
